import { Routes } from '@angular/router';
import {
  adminGuard,
  authGuard,
  blogPostResolver,
  hasOrganizationGuard,
  homeVehiclesResolver,
  hostProfileResolver,
  motorhomeSimilarResolver,
  onboardingGuard,
  orgPermissionGuard,
  profileCompleteGuard,
  profileResolver,
  regionVehiclesResolver,
} from '@guards/index';
import { alreadyHostGuard } from './core/guards/already-host.guard';
import { canDeactivateVehicle } from './core/guards/can-deactivate-vehicle.guard';

export const routes: Routes = [
  // ============================================================
  // PUBLIC — no authentication required
  // ============================================================
  {
    path: '',
    loadComponent: () => import('@pages/home/home.page').then((m) => m.HomePage),
    // Load featured inventory before render so the prerendered home page
    // carries server-rendered motorhome cards (HMM-162 IL-2).
    resolve: { featured: homeVehiclesResolver },
  },
  // /home 301-redirects to / at the Firebase Hosting edge (firebase.json).
  // The Angular-level redirect catches the same path during in-app
  // navigation so we never render a duplicate indexable homepage copy.
  {
    path: 'home',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'sign-in',
    loadComponent: () => import('@pages/sign-in/sign-in.page').then((m) => m.SignInPage),
  },
  {
    path: 'sign-up',
    loadComponent: () => import('@pages/sign-up/sign-up.page').then((m) => m.SignUpPage),
  },
  {
    path: 'accept-invitation/:id',
    loadComponent: () =>
      import('@pages/accept-invitation/accept-invitation.page').then((m) => m.AcceptInvitationPage),
  },
  {
    path: 'join',
    loadComponent: () => import('@pages/join-org/join-org.page').then((m) => m.JoinOrgPage),
  },
  {
    path: 'terms',
    loadComponent: () => import('@pages/terms/terms.page').then((m) => m.TermsPage),
  },
  {
    path: 'privacy',
    loadComponent: () => import('@pages/privacy/privacy.page').then((m) => m.PrivacyPage),
  },
  {
    path: 'cookies',
    loadComponent: () => import('@pages/cookies/cookies.page').then((m) => m.CookiesPage),
  },
  // Company
  {
    path: 'about',
    loadComponent: () => import('@pages/about/about.page').then((m) => m.AboutPage),
  },
  {
    path: 'careers',
    loadComponent: () => import('@pages/careers/careers.page').then((m) => m.CareersPage),
  },
  {
    path: 'careers/job/:id',
    loadComponent: () =>
      import('@pages/careers/job-details/job-details.page').then((m) => m.JobDetailsPage),
  },
  {
    path: 'press',
    loadComponent: () => import('@pages/press/press.page').then((m) => m.PressPage),
  },
  {
    path: 'blog',
    loadComponent: () => import('@pages/blog/blog.page').then((m) => m.BlogPage),
  },
  {
    path: 'blog/:slug',
    loadComponent: () => import('@pages/blog/blog-post.page').then((m) => m.BlogPostPage),
    resolve: { post: blogPostResolver },
  },
  // Support
  {
    path: 'help',
    loadComponent: () => import('@pages/help/help.page').then((m) => m.HelpPage),
  },
  {
    path: 'safety',
    loadComponent: () => import('@pages/safety/safety.page').then((m) => m.SafetyPage),
  },
  {
    path: 'cancellation',
    loadComponent: () =>
      import('@pages/cancellation/cancellation.page').then((m) => m.CancellationPage),
  },
  {
    path: 'contact',
    loadComponent: () => import('@pages/contact/contact.page').then((m) => m.ContactPage),
  },
  // Hosting info
  {
    path: 'host-resources',
    loadComponent: () =>
      import('@pages/host-resources/host-resources.page').then((m) => m.HostResourcesPage),
  },
  {
    path: 'list-your-motorhome',
    loadComponent: () =>
      import('@pages/list-your-motorhome/list-your-motorhome.page').then(
        (m) => m.ListYourMotorhomePage,
      ),
  },
  {
    path: 'speedy-import',
    loadComponent: () =>
      import('@pages/speedy-import/speedy-import.page').then((m) => m.SpeedyImportPage),
  },
  {
    path: 'managing-your-team',
    loadComponent: () =>
      import('@pages/managing-your-team/managing-your-team.page').then(
        (m) => m.ManagingYourTeamPage,
      ),
  },
  {
    path: 'host-cancellation-policy',
    loadComponent: () =>
      import('@pages/host-cancellation-policy/host-cancellation-policy.page').then(
        (m) => m.HostCancellationPolicyPage,
      ),
  },
  {
    path: 'community',
    loadComponent: () => import('@pages/community/community.page').then((m) => m.CommunityPage),
  },
  {
    path: 'sitemap',
    loadComponent: () => import('@pages/sitemap/sitemap.page').then((m) => m.SitemapPage),
  },
  // Browse (public — auth state handled inside components)
  // /search/:region must come BEFORE /search so Angular's order-sensitive
  // matching picks the static landing page for known slugs (scotland,
  // london, newcastle) before falling back to the dynamic search page.
  {
    path: 'search/:region',
    loadComponent: () =>
      import('@pages/search-location/search-location.page').then((m) => m.SearchLocationPage),
    // Resolve active inventory before render so the prerendered region page
    // carries server-rendered motorhome cards (HMM-162 IL-1) — an in-component
    // live read raced the SSR stability snapshot.
    resolve: { vehicles: regionVehiclesResolver },
  },
  {
    // Renter-guide hub (HMM-162). Prerendered topical index linking every
    // guide; gives the guide breadcrumb a real "Guides" destination.
    path: 'guides',
    loadComponent: () => import('@pages/guides/guides.page').then((m) => m.GuidesPage),
  },
  {
    // Renter-intent SEO guides (HMM-163). One component handles every slug;
    // the prerender list iterates GUIDES so each guide gets a real HTML file.
    path: 'guides/:slug',
    loadComponent: () => import('@pages/guide/guide.page').then((m) => m.GuidePage),
  },
  {
    path: 'search',
    loadComponent: () => import('@pages/search/search.page').then((m) => m.SearchPage),
  },
  {
    path: 'favorites',
    loadComponent: () => import('@pages/favorites/favorites.page').then((m) => m.FavoritesPage),
  },
  {
    path: 'destinations',
    loadComponent: () =>
      import('@pages/destinations/destinations.page').then((m) => m.DestinationsPage),
  },
  // /destination/:name 301-redirects to /search/:name at the Firebase Hosting
  // edge (firebase.json). The Angular-level redirect below catches the same
  // path during in-app navigation so we never render the old destination page
  // ourselves; it was duplicating /search/:region and getting de-indexed.
  {
    path: 'destination/:name',
    redirectTo: 'search/:name',
    pathMatch: 'full',
  },
  {
    path: 'hosts/:slug',
    loadComponent: () =>
      import('@pages/host-profile/host-profile.page').then((m) => m.HostProfilePage),
    // Resolve the host org + admin + vehicles before render so the page's
    // meta tags, JSON-LD and <h1> land in the server-rendered HTML — the
    // async ngOnInit load ran after the SSR flush, shipping homepage meta
    // on every /hosts/:slug and getting them canonicalised to / (HMM-224).
    resolve: { hostProfile: hostProfileResolver },
  },
  {
    path: 'motorhome/:id',
    loadComponent: () => import('@pages/motorhome/motorhome.page').then((m) => m.MotorhomesPage),
    // Resolve the similar-vehicles pool before render so the related-listings
    // cards land in the server-rendered HTML (HMM-162 IL-2) — a child-level
    // read started after the SSR stability snapshot.
    resolve: { similar: motorhomeSimilarResolver },
  },
  {
    path: 'motorhome/:id/photos',
    loadComponent: () =>
      import('@pages/motorhome/motorhome-photos.page').then((m) => m.MotorhomePhotosPage),
  },
  // Account verify email — public so users can verify before fully authenticated
  {
    path: 'account/verify-email',
    loadComponent: () =>
      import('@pages/verify-email/verify-email.page').then((m) => m.VerifyEmailPage),
  },

  // ============================================================
  // AUTHENTICATED — any signed-in user
  // ============================================================
  {
    path: '',
    canActivate: [authGuard],
    children: [
      {
        path: 'onboarding',
        loadComponent: () =>
          import('@pages/onboarding/onboarding.page').then((m) => m.OnboardingPage),
        canActivate: [onboardingGuard],
      },
      {
        path: 'trips',
        loadComponent: () => import('@pages/trips/trips.page').then((m) => m.TripsPage),
      },
      {
        path: 'checkout',
        loadComponent: () =>
          import('./main/pages/payment-checkout/payment-checkout.component').then(
            (m) => m.PaymentCheckoutComponent,
          ),
        canActivate: [profileCompleteGuard],
        resolve: { user: profileResolver },
      },
      {
        path: 'checkout/:bookingId',
        loadComponent: () =>
          import('./main/pages/payment-checkout/payment-checkout.component').then(
            (m) => m.PaymentCheckoutComponent,
          ),
        resolve: { user: profileResolver },
      },
      {
        path: 'booking-extras/:vehicleId',
        loadComponent: () =>
          import('./main/pages/booking-extras/booking-extras.page').then(
            (m) => m.BookingExtrasPage,
          ),
        resolve: { user: profileResolver },
      },
      {
        path: 'booking/:id',
        redirectTo: 'bookings/:id',
        pathMatch: 'full',
      },
      {
        path: 'bookings/:id',
        loadComponent: () =>
          import('./main/pages/booking-details/booking-details.page').then(
            (m) => m.BookingDetailsPage,
          ),
        resolve: { user: profileResolver },
      },
      {
        path: 'reviews',
        loadComponent: () => import('@pages/reviews/reviews.page').then((m) => m.ReviewsPage),
        resolve: { user: profileResolver },
      },
      {
        path: 'trips/:bookingId/review',
        loadComponent: () =>
          import('./main/pages/reviews/write-review/write-review.page').then(
            (m) => m.WriteReviewPage,
          ),
        resolve: { user: profileResolver },
        data: { isHost: false },
      },
      {
        path: 'reviews/submit/:bookingId',
        loadComponent: () =>
          import('./main/pages/reviews/write-review/write-review.page').then(
            (m) => m.WriteReviewPage,
          ),
        resolve: { user: profileResolver },
        data: { isHost: false },
      },
      {
        path: 'profile',
        loadComponent: () => import('@pages/profile/profile.page').then((m) => m.ProfilePage),
        resolve: { user: profileResolver },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@pages/profile/profile-overview.page').then((m) => m.ProfileOverviewPage),
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('@pages/profile/profile-settings.page').then((m) => m.ProfileSettingsPage),
          },
        ],
      },
      {
        path: 'account/verify-phone',
        loadComponent: () =>
          import('@pages/verify-phone/verify-phone.page').then((m) => m.VerifyPhonePage),
        resolve: { user: profileResolver },
      },
      {
        path: 'account/payment-methods',
        loadComponent: () =>
          import('@pages/payment-methods/payment-methods.page').then((m) => m.PaymentMethodsPage),
        resolve: { user: profileResolver },
      },
      {
        path: 'account/verify-identity',
        loadComponent: () =>
          import('@pages/verify-identity/verify-identity.page').then((m) => m.VerifyIdentityPage),
        resolve: { user: profileResolver },
      },
      {
        path: 'become-a-host',
        loadComponent: () =>
          import('@pages/become-a-host/become-a-host.page').then((m) => m.BecomeAHostPage),
        canActivate: [alreadyHostGuard],
        resolve: { user: profileResolver },
      },
      // Legacy motorhome management (pre-host-dashboard)
      {
        path: 'host-dashboard/add-motorhome',
        loadComponent: () =>
          import('./main/pages/add-motorhome/add-motorhome.page').then((m) => m.AddMotorhomePage),
        resolve: { user: profileResolver },
        canDeactivate: [canDeactivateVehicle],
      },
      {
        path: 'host-dashboard/edit-motorhome/:id',
        loadComponent: () =>
          import('./main/pages/add-motorhome/add-motorhome.page').then((m) => m.AddMotorhomePage),
        resolve: { user: profileResolver },
        canDeactivate: [canDeactivateVehicle],
      },
      // Stripe Connect onboarding
      {
        path: 'host/connect',
        loadComponent: () =>
          import('./main/pages/connect-onboarding/connect-onboarding.component').then(
            (m) => m.ConnectOnboardingComponent,
          ),
        resolve: { user: profileResolver },
      },
      {
        path: 'host/connect/complete',
        loadComponent: () =>
          import('./main/pages/connect-complete/connect-complete.component').then(
            (m) => m.ConnectCompleteComponent,
          ),
        resolve: { user: profileResolver },
      },
      {
        path: 'host/connect/refresh',
        loadComponent: () =>
          import('./main/pages/connect-onboarding/connect-onboarding.component').then(
            (m) => m.ConnectOnboardingComponent,
          ),
        resolve: { user: profileResolver },
      },
      {
        path: 'host/earnings',
        loadComponent: () =>
          import('./main/pages/earnings-dashboard/earnings-dashboard.component').then(
            (m) => m.EarningsDashboardComponent,
          ),
        canActivate: [orgPermissionGuard],
        data: { requiredPermission: 'viewFinancials' },
        resolve: { user: profileResolver },
      },
    ],
  },

  // ============================================================
  // HOST — requires active organization (or admin)
  // ============================================================
  {
    path: 'host',
    loadComponent: () =>
      import('./main/layouts/host-layout/host-layout.component').then((m) => m.HostLayoutComponent),
    canActivate: [hasOrganizationGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./main/pages/host/overview/host-overview.page').then((m) => m.HostOverviewPage),
      },
      {
        path: 'vehicles',
        loadComponent: () =>
          import('./main/pages/host/vehicles/host-vehicles.page').then((m) => m.HostVehiclesPage),
      },
      {
        path: 'vehicles/:id/calendar',
        loadComponent: () =>
          import('./main/pages/host/vehicle-calendar/vehicle-calendar.page').then(
            (m) => m.VehicleCalendarPage,
          ),
      },
      {
        path: 'listings/:listingId/fleet-calendar',
        loadComponent: () =>
          import('./main/pages/host/fleet-calendar/fleet-calendar.page').then(
            (m) => m.FleetCalendarPage,
          ),
        canActivate: [orgPermissionGuard],
        data: { requiredPermission: 'manageSettings' },
      },
      {
        path: 'bookings',
        loadComponent: () =>
          import('./main/pages/host/bookings/host-bookings.page').then((m) => m.HostBookingsPage),
      },
      {
        path: 'bookings/:bookingId/review',
        loadComponent: () =>
          import('./main/pages/reviews/write-review/write-review.page').then(
            (m) => m.WriteReviewPage,
          ),
        data: { isHost: true },
      },
      {
        path: 'calendar',
        loadComponent: () =>
          import('./main/pages/host/calendar-overview/calendar-overview.page').then(
            (m) => m.CalendarOverviewPage,
          ),
      },
      {
        path: 'calendar-sync',
        loadComponent: () =>
          import('./main/pages/host/calendar-sync/calendar-sync.page').then(
            (m) => m.CalendarSyncPage,
          ),
      },
      {
        path: 'reviews',
        loadComponent: () =>
          import('./main/pages/host/reviews/host-reviews.page').then((m) => m.HostReviewsPage),
      },
      {
        path: 'team',
        loadComponent: () =>
          import('./main/pages/host/team/host-team.page').then((m) => m.HostTeamPage),
        canActivate: [orgPermissionGuard],
        data: { requiredPermission: 'manageTeam' },
      },
      {
        path: 'payouts',
        loadComponent: () =>
          import('./main/pages/host/payouts/host-payouts.page').then((m) => m.HostPayoutsPage),
        canActivate: [orgPermissionGuard],
        data: { requiredPermission: 'viewFinancials' },
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./main/pages/host/settings/host-settings.page').then((m) => m.HostSettingsPage),
        canActivate: [orgPermissionGuard],
        data: { requiredPermission: 'manageSettings' },
      },
      {
        path: 'locations',
        loadComponent: () =>
          import('./main/pages/host/locations/host-locations.page').then(
            (m) => m.HostLocationsPage,
          ),
      },
      {
        path: 'extras',
        loadComponent: () =>
          import('./main/pages/host/extras/host-extras.page').then((m) => m.HostExtrasPage),
      },
    ],
  },
  // Legacy host-dashboard redirects
  { path: 'host-dashboard', redirectTo: '/host', pathMatch: 'full' },
  { path: 'host-dashboard/overview', redirectTo: '/host', pathMatch: 'full' },
  { path: 'host-dashboard/bookings', redirectTo: '/host/bookings', pathMatch: 'full' },
  { path: 'host-dashboard/motorhomes', redirectTo: '/host/vehicles', pathMatch: 'full' },
  { path: 'host-dashboard/reviews', redirectTo: '/host/reviews', pathMatch: 'full' },
  { path: 'host-dashboard/:tab', redirectTo: '/host', pathMatch: 'full' },

  // ============================================================
  // ADMIN — platform admin only
  // ============================================================
  {
    path: 'admin',
    canActivate: [adminGuard],
    resolve: { user: profileResolver },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./main/pages/admin/admin-dashboard.page').then((m) => m.AdminDashboardPage),
      },
      {
        path: 'cancellation-breakdown',
        loadComponent: () =>
          import('@pages/admin-cancellation-breakdown/admin-cancellation-breakdown.page').then(
            (m) => m.AdminCancellationBreakdownPage,
          ),
      },
      {
        path: 'host-verification',
        loadComponent: () =>
          import('./main/pages/admin/host-verification/host-verification.page').then(
            (m) => m.HostVerificationPage,
          ),
      },
      {
        path: 'users',
        loadComponent: () => import('./main/pages/admin/users/users.page').then((m) => m.UsersPage),
      },
      {
        path: 'inbox',
        loadComponent: () => import('./main/pages/admin/inbox/inbox.page').then((m) => m.InboxPage),
      },
      { path: 'contact-messages', redirectTo: 'inbox', pathMatch: 'full' },
      {
        path: 'analytics',
        loadComponent: () =>
          import('./main/pages/admin/analytics/admin-analytics.page').then(
            (m) => m.AdminAnalyticsPage,
          ),
      },
      {
        path: 'rental-insights',
        loadComponent: () =>
          import('./main/pages/admin/analytics/rental-insights.page').then(
            (m) => m.RentalInsightsPage,
          ),
      },
      {
        path: 'financial-tracking',
        loadComponent: () =>
          import('./main/pages/admin/financial-tracking/financial-tracking.page').then(
            (m) => m.FinancialTrackingPage,
          ),
      },
      {
        path: 'log-investigator',
        loadComponent: () =>
          import('./main/pages/admin/log-investigator/log-investigator.page').then(
            (m) => m.LogInvestigatorPage,
          ),
      },
      {
        path: 'payouts',
        loadComponent: () =>
          import('./main/pages/admin/payouts/payouts.page').then((m) => m.PayoutsPage),
      },
      {
        path: 'function-test',
        loadComponent: () =>
          import('./main/pages/function-test/function-test.page').then((m) => m.FunctionTestPage),
      },
      {
        path: 'vehicle-approval',
        loadComponent: () =>
          import('./main/pages/admin/vehicle-approval/vehicle-approval.page').then(
            (m) => m.VehicleApprovalPage,
          ),
      },
      {
        path: 'vehicle-management',
        loadComponent: () =>
          import('./main/pages/admin/vehicle-management/vehicle-management.page').then(
            (m) => m.VehicleManagementPage,
          ),
      },
      {
        path: 'organization-management',
        loadComponent: () =>
          import('./main/pages/admin/organization-management/organization-management.page').then(
            (m) => m.OrganizationManagementPage,
          ),
      },
      {
        path: 'sendgrid',
        loadComponent: () =>
          import('./main/pages/admin/sendgrid-test/sendgrid-test.page').then(
            (m) => m.SendGridTestPage,
          ),
      },
      // Blog management
      {
        path: 'blog',
        loadComponent: () =>
          import('./main/pages/admin/blog/blog-posts-list.page').then((m) => m.BlogPostsListPage),
      },
      {
        path: 'blog/new',
        loadComponent: () =>
          import('./main/pages/admin/blog/blog-post-editor.page').then((m) => m.BlogPostEditorPage),
      },
      {
        path: 'blog/:id',
        loadComponent: () =>
          import('./main/pages/admin/blog/blog-post-editor.page').then((m) => m.BlogPostEditorPage),
      },
      // CRM
      {
        path: 'crm',
        loadComponent: () =>
          import('./main/pages/admin/crm/crm-dashboard.page').then((m) => m.CrmDashboardPage),
      },
      {
        path: 'crm/prospects',
        loadComponent: () =>
          import('./main/pages/admin/crm/prospects/crm-prospects-list.page').then(
            (m) => m.CrmProspectsListPage,
          ),
      },
      {
        path: 'crm/templates',
        loadComponent: () =>
          import('./main/pages/admin/crm/templates/email-templates-list.page').then(
            (m) => m.EmailTemplatesListPage,
          ),
      },
      {
        path: 'crm/templates/new',
        loadComponent: () =>
          import('./main/pages/admin/crm/templates/template-editor.page').then(
            (m) => m.TemplateEditorPage,
          ),
      },
      {
        path: 'crm/templates/:id',
        loadComponent: () =>
          import('./main/pages/admin/crm/templates/template-editor.page').then(
            (m) => m.TemplateEditorPage,
          ),
      },
      {
        path: 'crm/campaigns',
        loadComponent: () =>
          import('./main/pages/admin/crm/campaigns/campaigns-list.page').then(
            (m) => m.CampaignsListPage,
          ),
      },
      {
        path: 'crm/campaigns/new',
        loadComponent: () =>
          import('./main/pages/admin/crm/campaigns/campaign-builder.page').then(
            (m) => m.CampaignBuilderPage,
          ),
      },
      {
        path: 'crm/reports',
        loadComponent: () =>
          import('./main/pages/admin/crm/reports/crm-reports.page').then((m) => m.CrmReportsPage),
      },
    ],
  },
];
